import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Image,
  Spinner,
  Modal,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { resolveProfilePhoto } from "../utils/profilePhoto";
import { updateStoredUser } from "../utils/sessionUser";

/* ===== API endpoints ===== */
const API_GET_ME = "/api/employee/profile/me";
const API_UPDATE = "/api/employee/profile/update";            // multipart: fullName,email,mobile,photo
const API_CHANGE_PASSWORD = "/api/employee/profile/password"; // {currentPassword,newPassword}
const API_SAVE_DOCS = "/api/employee/profile/documents";      // multipart: aadhar,pan,addressProof,resume (optional if you add BE)
const API_SAVE_BANK = "/api/employee/profile/bank";           // {accountName,accountNumber,ifsc,bankName,branch}

const EmployeeProfile = () => {
  /* ---------- state ---------- */
  const [profile, setProfile] = useState({
    fullName: "",
    email: "",
    mobile: "",
    photo: "",
    photoVersion: 0,
  });
  const [initialProfile, setInitialProfile] = useState(null);
  const [bank, setBank] = useState({
    accountName: "",
    accountNumber: "",
    ifsc: "",
    bankName: "",
    branch: "",
  });
  const [initialBank, setInitialBank] = useState(null);
  const [bankSaving, setBankSaving] = useState(false);

  // photo handling
  const [photoFile, setPhotoFile] = useState(null);
  const [imgFail, setImgFail] = useState(false);

  const safePreview = useMemo(() => {
    if (photoFile instanceof File) return URL.createObjectURL(photoFile);
    return resolveProfilePhoto(profile.photo, profile.photoVersion);
  }, [photoFile, profile.photo, profile.photoVersion]);

  // docs
  const [docs, setDocs] = useState({
    aadhar: null,
    pan: null,
    addressProof: null,
    resume: null,
  });
  const [docsUploading, setDocsUploading] = useState(false);

  // loaders
  const [loadingMe, setLoadingMe] = useState(true);
  const [saving, setSaving] = useState(false);

  // password
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", show: false });
  const [pwLoading, setPwLoading] = useState(false);

  // axios with token
  const token = localStorage.getItem("token");
  const client = useMemo(() => {
    const c = axios.create();
    c.interceptors.request.use((config) => {
      config.headers = config.headers || {};
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    return c;
  }, [token]);

  /* ---------- fetch me ---------- */
  useEffect(() => {
    (async () => {
      try {
        setLoadingMe(true);
        const { data } = await client.get(API_GET_ME);
        const me = {
          fullName: data.fullName || "",
          email: data.email || "",
          mobile: data.mobile || "",
          photo: data.photo || "",
          photoVersion: Date.now(),
        };
        setProfile(me);
        setInitialProfile(me);
        updateStoredUser({
          fullName: me.fullName,
          email: me.email,
          mobile: me.mobile,
          photo: me.photo,
          photoUpdatedAt: Date.now(),
        });
        const b = {
          accountName: data.bank?.accountName || "",
          accountNumber: data.bank?.accountNumber || "",
          ifsc: data.bank?.ifsc || "",
          bankName: data.bank?.bankName || "",
          branch: data.bank?.branch || "",
        };
        setBank(b);
        setInitialBank(b);
        setImgFail(false);
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to load employee profile");
      } finally {
        setLoadingMe(false);
      }
    })();

    return () => {
      if (photoFile && typeof safePreview === "string" && safePreview.startsWith("blob:")) {
        URL.revokeObjectURL(safePreview);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- derived ---------- */
  const isDirty = useMemo(() => {
    if (!initialProfile) return false;
    const basicChanged = ["fullName", "email", "mobile"].some(
      (k) => String(profile[k] || "") !== String(initialProfile[k] || "")
    );
    const photoChanged = !!photoFile;
    return basicChanged || photoChanged;
  }, [profile, initialProfile, photoFile]);

  const isBankDirty = useMemo(() => {
    if (!initialBank) return false;
    return ["accountName", "accountNumber", "ifsc", "bankName", "branch"].some(
      (k) => String(bank[k] || "") !== String(initialBank[k] || "")
    );
  }, [bank, initialBank]);

  /* ---------- handlers ---------- */
  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(png|jpe?g|webp|gif)/i.test(f.type)) {
      toast.warn("Only image files are allowed");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.warn("Max file size 2MB");
      return;
    }
    setPhotoFile(f);
    setImgFail(false);
  };

  const handleUpdate = async () => {
    if (!profile.fullName?.trim()) return toast.warn("Full name is required");
    if (!profile.email?.trim()) return toast.warn("Email is required");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("fullName", profile.fullName);
      fd.append("email", profile.email);
      fd.append("mobile", profile.mobile || "");
      if (photoFile instanceof File) fd.append("photo", photoFile);

      const { data } = await client.put(API_UPDATE, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const p = {
        fullName: data.fullName ?? profile.fullName,
        email: data.email ?? profile.email,
        mobile: data.mobile ?? profile.mobile,
        photo: data.photo ?? profile.photo,
        photoVersion: Date.now(),
      };
      setProfile(p);
      setInitialProfile(p);
      updateStoredUser({
        fullName: p.fullName,
        email: p.email,
        mobile: p.mobile,
        photo: p.photo,
        photoUpdatedAt: Date.now(),
      });
      setPhotoFile(null);
      setImgFail(false);
      toast.success("✅ Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleBankSave = async () => {
    setBankSaving(true);
    try {
      const payload = {
        accountName: bank.accountName || "",
        accountNumber: bank.accountNumber || "",
        ifsc: (bank.ifsc || "").toUpperCase(),
        bankName: bank.bankName || "",
        branch: bank.branch || "",
      };
      const { data } = await client.put(API_SAVE_BANK, payload);
      const updated = {
        accountName: data.bank?.accountName || payload.accountName,
        accountNumber: data.bank?.accountNumber || payload.accountNumber,
        ifsc: data.bank?.ifsc || payload.ifsc,
        bankName: data.bank?.bankName || payload.bankName,
        branch: data.bank?.branch || payload.branch,
      };
      setBank(updated);
      setInitialBank(updated);
      toast.success("🏦 Bank details saved");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save bank details");
    } finally {
      setBankSaving(false);
    }
  };

  const uploadDocs = async () => {
    // all are optional — send only provided
    const hasAny =
      docs.aadhar || docs.pan || docs.addressProof || docs.resume;
    if (!hasAny) return toast.info("Select at least one document");

    setDocsUploading(true);
    try {
      const fd = new FormData();
      if (docs.aadhar) fd.append("aadhar", docs.aadhar);
      if (docs.pan) fd.append("pan", docs.pan);
      if (docs.addressProof) fd.append("addressProof", docs.addressProof);
      if (docs.resume) fd.append("resume", docs.resume);

      await client.post(API_SAVE_DOCS, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("📄 Documents uploaded");
      setDocs({ aadhar: null, pan: null, addressProof: null, resume: null });
      // clear file inputs by resetting key (simple approach)
      setInputsKey((k) => k + 1);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to upload documents";
      toast.error(msg);
    } finally {
      setDocsUploading(false);
    }
  };

  const [inputsKey, setInputsKey] = useState(1); // to reset <input type=file>

  const handlePasswordChange = async () => {
    if (!pw.currentPassword || !pw.newPassword) return toast.warn("Both fields required");
    if (pw.newPassword.length < 6) return toast.warn("Password must be at least 6 chars");
    setPwLoading(true);
    try {
      await client.put(API_CHANGE_PASSWORD, {
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      });
      toast.success("🔒 Password updated");
      setPw({ currentPassword: "", newPassword: "", show: false });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Password update failed");
    } finally {
      setPwLoading(false);
    }
  };

  /* ---------- UI ---------- */
  return (
    <Container className="py-4">
      <div
        className="rounded-3 p-4 mb-4"
        style={{ background: "linear-gradient(135deg,#3b82f6 0%, #06b6d4 100%)", color: "#fff" }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h2 className="mb-1">Employee Profile</h2>
            <div className="opacity-75">Update your info, photo & documents</div>
          </div>
          <div>
            <Button variant="light" onClick={handleUpdate} disabled={!isDirty || saving}>
              {saving ? "Saving..." : isDirty ? "Save Changes" : "Saved"}
            </Button>
          </div>
        </div>
      </div>

      <Row className="g-4">
        {/* Profile Card */}
        <Col lg={7}>
          <Card className="shadow-sm">
            <Card.Header className="fw-semibold">
              Profile Details {loadingMe && <Spinner size="sm" animation="border" className="ms-2" />}
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                <Col md={4} className="text-center">
                  <div className="position-relative d-inline-block">
                    {!imgFail && safePreview ? (
                      <Image
                        src={safePreview}
                        alt="Employee"
                        roundedCircle
                        onError={() => setImgFail(true)}
                        style={{ width: 140, height: 140, objectFit: "cover", boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: 140, height: 140, background: "#e5e7eb", color: "#374151", fontWeight: 700 }}
                      >
                        {profile.fullName?.[0]?.toUpperCase() || "E"}
                      </div>
                    )}

                    <Form.Label
                      htmlFor="photoInput"
                      className="position-absolute bottom-0 end-0 btn btn-sm btn-primary rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-camera" />
                    </Form.Label>
                    <Form.Control
                      id="photoInput"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={onPickPhoto}
                      className="d-none"
                    />
                  </div>
                  <div className="mt-2 small text-muted">Max 2MB. PNG/JPG/WebP/GIF.</div>
                </Col>

                <Col md={8}>
                  <Form.Group className="mb-3" controlId="fullName">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={profile.fullName}
                      onChange={(e) => setProfile((p) => ({ ...p, fullName: e.target.value }))}
                      placeholder="Your full name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                      placeholder="name@example.com"
                    />
                  </Form.Group>

                  <Form.Group className="mb-0" controlId="mobile">
                    <Form.Label>Mobile</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>+91</InputGroup.Text>
                      <Form.Control
                        type="tel"
                        value={profile.mobile}
                        onChange={(e) => setProfile((p) => ({ ...p, mobile: e.target.value }))}
                        placeholder="10-digit mobile"
                      />
                    </InputGroup>
                  </Form.Group>

                  <div className="d-flex gap-2 mt-3">
                    <Button onClick={handleUpdate} disabled={!isDirty || saving}>
                      {saving ? "Saving..." : "Update Profile"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={async () => {
                        try {
                          setLoadingMe(true);
                          const { data } = await client.get(API_GET_ME);
                          const p = {
                            fullName: data.fullName || "",
                            email: data.email || "",
                            mobile: data.mobile || "",
                            photo: data.photo || "",
                            photoVersion: Date.now(),
                          };
                          setProfile(p);
                          setInitialProfile(p);
                          updateStoredUser({
                            fullName: p.fullName,
                            email: p.email,
                            mobile: p.mobile,
                            photo: p.photo,
                            photoUpdatedAt: Date.now(),
                          });
                          setPhotoFile(null);
                          setImgFail(false);
                          toast.info("Refreshed");
                        } catch (err) {
                          toast.error(err?.response?.data?.message || "Failed to refresh");
                        } finally {
                          setLoadingMe(false);
                        }
                      }}
                      disabled={loadingMe || saving}
                    >
                      Refresh
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Security */}
        <Col lg={5}>
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold d-flex justify-content-between align-items-center">
              <span>Security</span>
              <Badge bg="secondary">Change Password</Badge>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3" controlId="currentPassword">
                  <Form.Label>Current Password</Form.Label>
                  <Form.Control
                    type={pw.show ? "text" : "password"}
                    value={pw.currentPassword}
                    onChange={(e) => setPw((s) => ({ ...s, currentPassword: e.target.value }))}
                    placeholder="Enter current password"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="newPassword">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type={pw.show ? "text" : "password"}
                    value={pw.newPassword}
                    onChange={(e) => setPw((s) => ({ ...s, newPassword: e.target.value }))}
                    placeholder="Enter new password"
                  />
                </Form.Group>
                <div className="d-flex align-items-center gap-3">
                  <Form.Check
                    type="checkbox"
                    id="showPw"
                    label="Show"
                    checked={pw.show}
                    onChange={(e) => setPw((s) => ({ ...s, show: e.target.checked }))}
                  />
                  <Button onClick={handlePasswordChange} disabled={pwLoading}>
                    {pwLoading ? "Updating..." : "Change Password"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Bank Details */}
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
              <span>Bank Details</span>
              <small className="text-muted">Used for salary credits</small>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Account Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={bank.accountName}
                      onChange={(e) => setBank((b) => ({ ...b, accountName: e.target.value }))}
                      placeholder="As per bank records"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Account Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={bank.accountNumber}
                      onChange={(e) => setBank((b) => ({ ...b, accountNumber: e.target.value }))}
                      placeholder="XXXXXXXXXXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>IFSC</Form.Label>
                    <Form.Control
                      type="text"
                      value={bank.ifsc}
                      onChange={(e) => setBank((b) => ({ ...b, ifsc: e.target.value.toUpperCase() }))}
                      placeholder="IFSC code"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={bank.bankName}
                      onChange={(e) => setBank((b) => ({ ...b, bankName: e.target.value }))}
                      placeholder="Bank name"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Branch</Form.Label>
                    <Form.Control
                      type="text"
                      value={bank.branch}
                      onChange={(e) => setBank((b) => ({ ...b, branch: e.target.value }))}
                      placeholder="Branch"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="mt-3 d-flex gap-2">
                <Button variant="success" onClick={handleBankSave} disabled={!isBankDirty || bankSaving}>
                  {bankSaving ? "Saving..." : "Save Bank Details"}
                </Button>
                <small className="text-muted">
                  Please double-check account number and IFSC.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Documents */}
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
              <span>Documents</span>
              <small className="text-muted">Aadhaar / PAN / Address Proof / Resume</small>
            </Card.Header>
            <Card.Body>
              <Row className="g-3" key={inputsKey}>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Aadhaar</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setDocs((d) => ({ ...d, aadhar: e.target.files?.[0] || null }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>PAN</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setDocs((d) => ({ ...d, pan: e.target.files?.[0] || null }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Address Proof</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setDocs((d) => ({ ...d, addressProof: e.target.files?.[0] || null }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Resume / CV</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setDocs((d) => ({ ...d, resume: e.target.files?.[0] || null }))}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="mt-3 d-flex gap-2">
                <Button variant="success" onClick={uploadDocs} disabled={docsUploading}>
                  {docsUploading ? "Uploading..." : "Upload Documents"}
                </Button>
                <small className="text-muted">
                  Tip: Images/PDF up to a few MB each are fine.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ToastContainer
        position="top-right"
        autoClose={2200}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Container>
  );
};

export default EmployeeProfile;
